import { useKeycloak } from '@react-keycloak/web'
import { FormEvent, useState } from 'react'
import { APP_NAME, KeycloakActionType, MenuLinks } from '../../constants'
import { ValidationErrors } from '../hooks/use-edit-form'
import { COUNCIL_NOT_ENABLED, getForwardingPath, getUserIDP } from '../services/auth-utils'
import { maybeAnEmailAddress } from '../services/form-utils'
import keycloakActions, { KeycloakActionConfig } from '../services/keycloak-actions'
import { useLocation, useNavigate } from 'react-router-dom'
import {
  Button,
  ButtonsContainer,
  FormField,
  Heading,
  InlineNotification,
  Link,
  Modal,
  TextInput,
  useTitleChange,
} from '@msaf/core-react'
interface SignInModalInterface {
  isOpen: boolean
  closeModal: () => void
}

export default function SignInModal({ isOpen, closeModal }: SignInModalInterface) {
  const [loading, setLoading] = useState<boolean>(false)
  const [councilWarning, setCouncilWarning] = useState<boolean>(false)
  const [validationErrors, setValidationErrors] = useState<ValidationErrors>({})
  // Reset page title to default:
  useTitleChange(APP_NAME)
  const { keycloak } = useKeycloak()
  const { pathname, search } = useLocation()
  const navigate = useNavigate()

  const resetState = () => {
    setLoading(false)
    setValidationErrors({})
    setCouncilWarning(false)
    return true
  }

  const handleCancel = () => {
    resetState()
    closeModal()
    navigate(pathname) // Clear homepage modal query params if any
    return true
  }

  if (search.includes('login=true')) {
    // modal should be open
    isOpen = true
  }

  let email = ''

  async function login(e: FormEvent) {
    e.preventDefault()
    resetState()

    // TextInput only runs setValue on blur, so when a user hits enter on the form the setValue
    // hasn't fired yet. Grabbing the email field value directly for now, this should be fixed
    // in core-react
    const emailEl: HTMLInputElement | null = e?.currentTarget?.querySelector('#text-field-email')
    const email = emailEl?.value ?? ''

    // Loose validation of email address
    const emailProvided = maybeAnEmailAddress(email)
    if (!emailProvided) {
      setValidationErrors({ email: ['Please provide a valid email address'] })
      setLoading(false)
      return
    }

    const idpHint = await getUserIDP(email.trim())
    if (idpHint === COUNCIL_NOT_ENABLED) {
      setLoading(false)
      setCouncilWarning(true)
      return
    }

    const loginConfig: KeycloakActionConfig = {
      idpHint,
      loginHint: email.trim(),
      action: KeycloakActionType.SIGNIN,
      keycloak,
      redirectPath: !pathname || pathname === '/' ? getForwardingPath() || MenuLinks.DASHBOARD : pathname,
    }
    keycloakActions(loginConfig)
  }

  return (
    <Modal isOpen={isOpen} contentLabel='Enter your email to login' onRequestClose={() => {}}>
      <Heading level={1}>Enter your email</Heading>
      <form onSubmit={login}>
        <FormField
          validationMessages={validationErrors?.email}
          labelId='login-email'
          label='Enter your email to continue'
        >
          <TextInput autoFocus={true} id='text-field-email' value={email} setValue={(value) => (email = value)} />
        </FormField>
        <ButtonsContainer containerStyle='right'>
          <Button type='submit' label='Login' buttonStyle='primary' isLoading={loading} isDisabled={loading} />
          <Button label='Cancel' buttonStyle='text-action' onClick={handleCancel} />
        </ButtonsContainer>
      </form>
      {councilWarning && (
        <InlineNotification messageType='warning' isDismissable={false}>
          Your council has not yet registered with Wells Aotearoa NZ. If you still want to create an account, use a
          non-council email in the&nbsp;
          <Link path='/?register=true' onClick={handleCancel}>
            registration form.
          </Link>
        </InlineNotification>
      )}
    </Modal>
  )
}
